import React, {FC} from 'react'
import {Link, useLocation, useParams} from 'react-router-dom'
import StatusButton from 'src/app/pages/orders/StatusButton'
import {PageLink, PageTitle} from 'src/marketConnect/layout/core'
import {useAppSelector} from 'src/redux/storeHooks'
import {usePayoutDetailData} from './usePayoutDetailData'
import {PayoutDetailNavigationState} from './types'
import {
  formatCurrency,
  formatPayoutRange,
  getSyncDocumentNo,
  normalizePayoutStatus,
  parseApiMessage,
} from './utils'
import Loader from 'src/utils/Loader'

const DetailRow: FC<{label: string; value: React.ReactNode; highlight?: boolean}> = ({
  label,
  value,
  highlight,
}) => (
  <div
    className={`d-flex align-items-center justify-content-between px-5 py-3 border-bottom ${
      highlight ? 'bg-light' : ''
    }`}
  >
    <div className={highlight ? 'fw-bold text-dark' : 'text-gray-700'}>{label}</div>
    <div className={highlight ? 'fw-bold text-dark text-end' : 'fw-semibold text-dark text-end'}>
      {value}
    </div>
  </div>
)

export const PayoutLogDetailPage: FC = () => {
  const {
    platformId,
    id: routePlatformId,
    payoutDate,
  } = useParams<{
    platformId: string
    id: string
    payoutDate: string
  }>()
  const location = useLocation()
  const navigationState = location.state as PayoutDetailNavigationState | null
  const backPath =
    navigationState?.returnTo ||
    (platformId && routePlatformId
      ? `/payout-logs/${platformId}/${routePlatformId}`
      : '/payout-logs')
  const {fetchedConnectedPlatformData} = useAppSelector((state) => state.fetchConnectedPlatform)
  const {data, loading, error} = usePayoutDetailData(routePlatformId, payoutDate)
  const summary = data?.summary
  const totals = data?.financial_totals
  const rows = data?.rows || []
  const payoutCurrency = totals?.currency || rows.find((row) => row?.currency)?.currency || 'USD'
  const titleId = String(
    navigationState?.settlementId ||
      summary?.grouped_payout_id ||
      summary?.settlement_id ||
      summary?.payout_id ||
      '-'
  )
  const syncDocumentNo = getSyncDocumentNo(
    summary?.sync_documents || summary?.sync_doc || summary?.qbo_check
  )
  const summaryMessage = Array.isArray(summary?.error_messages)
    ? summary?.error_messages.filter(Boolean).join('\n')
    : ''
  const matchedPlatform = (fetchedConnectedPlatformData || []).find(
    (platform: any) => String(platform?.id) === String(routePlatformId)
  )
  const platformName =
    matchedPlatform?.display_name ||
    matchedPlatform?.platform_info?.name ||
    matchedPlatform?.name ||
    'Platform'
  const pageTitle = `Payout Logs - ${platformName} - ${titleId}`
  const pageBreadcrumbs: PageLink[] = [
    {title: 'Sales', path: '/orders-all', isActive: false},
    {title: 'Payout Logs', path: backPath, isActive: false},
    {title: titleId, path: '', isActive: true},
  ]

  return (
    <>
      <PageTitle breadcrumbs={pageBreadcrumbs}>{pageTitle}</PageTitle>
      <div className='container-fluid px-0 py-2'>
        <div className='d-flex align-items-center justify-content-between mb-4'>
          <h1 className='text-dark fw-bold fs-5 my-0'>{pageTitle}</h1>
          <Link className='btn btn-sm btn-light' to={backPath}>
            <i className='bi bi-arrow-left me-2'></i>
            Back
          </Link>
        </div>

        {error && (
          <div className='alert alert-danger d-flex align-items-center mb-4' role='alert'>
            <i className='bi bi-exclamation-triangle-fill me-3 fs-3'></i>
            <div>{error}</div>
          </div>
        )}

        {loading ? (
          <div className='card'>
            <div className='card-body text-center py-10'>
              <Loader variant="section" />
            </div>
          </div>
        ) : (
          <>
            <div className='row g-6 mb-6'>
              <div className='col-12 col-xl-6'>
                <div className='card h-100'>
                  <div className='card-header'>
                    <h3 className='card-title fw-bold fs-6'>Payout Summary</h3>
                  </div>
                  <div className='card-body p-0'>
                    <DetailRow
                      label='Payout Range'
                      value={formatPayoutRange(summary?.payout_range)}
                    />
                    <DetailRow
                      label='Sync Status'
                      value={
                        <StatusButton
                          syncStatus={normalizePayoutStatus(summary?.sync_status) || 'N/A'}
                          syncMessage={parseApiMessage(summaryMessage) || undefined}
                        />
                      }
                    />
                    <DetailRow label='Sync Document No' value={syncDocumentNo} />
                  </div>
                </div>
              </div>

              <div className='col-12 col-xl-6'>
                <div className='card h-100'>
                  <div className='card-header'>
                    <h3 className='card-title fw-bold fs-6'>Financial Totals</h3>
                  </div>
                  <div className='card-body p-0'>
                    <DetailRow
                      label='Total Sales (Gross)'
                      value={formatCurrency(totals?.total_sales, payoutCurrency)}
                    />
                    <DetailRow
                      label='Total Fees'
                      value={
                        <span className={(totals?.total_fees || 0) < 0 ? 'text-danger' : ''}>
                          {formatCurrency(totals?.total_fees, payoutCurrency)}
                        </span>
                      }
                    />
                    <DetailRow
                      label='Net Deposit'
                      value={formatCurrency(totals?.net_deposit, payoutCurrency)}
                      highlight
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className='card'>
              <div className='card-header'>
                <h3 className='card-title fw-bold fs-6'>Order Payout Summary</h3>
              </div>
              <div className='card-body p-0'>
                <div className='table-responsive'>
                  <table className='table table-row-bordered align-middle mb-0'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='px-5 py-4'>Order ID</th>
                        <th className='px-5 py-4 text-end'>Order Total</th>
                        <th className='px-5 py-4 text-end'>Net Payout</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={3} className='text-center text-muted py-8'>
                            No data available
                          </td>
                        </tr>
                      ) : (
                        rows.map((row, index) => (
                          <tr key={row?.id || index}>
                            <td className='px-5 py-4'>{row?.order_id || row?.source_id || '-'}</td>
                            <td className='px-5 py-4 text-end'>
                              {formatCurrency(
                                row?.order_total ?? row?.amount,
                                row?.currency || 'USD'
                              )}
                            </td>
                            <td className='px-5 py-4 text-end'>
                              {formatCurrency(row?.net_payout, row?.currency || 'USD')}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
